import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Icon, IconColour } from '../icon';
import moment from 'moment';
import { DropDownMenu } from '../dropDownMenu';
import path from 'path';
import prettyBytes from 'pretty-bytes';
import axios from 'axios';
import { VideoPlayer } from './videoPlayer';
import { Text, Box, Button, Flex } from 'rebass/styled-components';
import useVideo from 'hooks/useVideo';
import Modal from '../modal';
import firebase from 'firebase/app';
import { deleteModal } from 'util/modal';
import { ExclamationIcon } from '../exclamationIcon';
import { Input } from '@rebass/forms';
import ProjectModal from 'components/asset/projectsModal';
import useRecords from 'hooks/useRecords';
import { useSelector } from 'react-redux';
import { AppState } from 'store';
import MovingModal from 'components/asset/movingModal';
import { DropdownItemsType } from 'util/types';
import RowIconButton from '../shell/RowIconButton';
import CategorySelect from '../shell/CategorySelect';
import { videoGrid } from 'util/ui';
import useLabelNames from 'hooks/useLabelNames';
import { PlayIcon, DownloadIcon, DeleteIcon, MoveIcon, IceIcon, FileIcon } from '../shell/Icons';

const InputContainer = styled(Box)<{ width: string }>`
  width: ${(p) => p.width};
  min-width: ${(p) => (p.width === '100%' ? '150px' : p.width)};
  box-sizing: border-box;
`;

const WarningInputContainer = styled(Box)<{ width: string }>`
  width: ${(p) => p.width};
  min-width: ${(p) => (p.width === '100%' ? '150px' : p.width)};
  box-sizing: border-box;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

// UI refresh: a compact amber "?" pill; the full question is the tooltip.
const WarningMessage = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  background-color: #fef4d8;
  color: #7a5300;
  width: fit-content;
  border-radius: 999px;
  padding: 2px 10px 2px 4px;
  margin-left: 12px;
  white-space: nowrap;
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: help;
`;

// A small amber "?" reminder on Preview files only (Aaron, 5 Nov 2026); the question is in the tooltip.
const WarningDot = styled.span`
  flex: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fef4d8;
  border: 1px solid #f2c46b;
  color: #7a5300;
  font-weight: 700;
  font-size: 10px !important;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
`;

// Every cell in a file row: 13px, one text colour (Aaron, 5 Nov 2026). The Label pill keeps its colour.
const CELL_COLOR = '#2E333B';

const NotesInput = styled(Input)`
  border: none;
  outline: none;
  font-size: 13px !important;
  color: ${CELL_COLOR};
  ::placeholder {
    color: #8a909b;
  }
`;

const Container = styled(Box)<{ isPreview: boolean }>`
  position: relative;
  border-bottom: 1px solid #eef0f2;
  :nth-child(odd) {
    background-color: #ffffff;
  }
  :nth-child(even) {
    background-color: #f9fafb;
  }
`;

const VideoBar = styled(Box)`
  height: 48px;
  position: relative;
`;

const LoadingBar = styled(Box)<{ percent: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${(p) => p.theme.colors.green};
  transform-origin: left;
  transform: scaleX(${(p) => p.percent / 100});
  opacity: ${(p) => (p.percent === 0 || p.percent === 100 ? 0 : 0.2)};
  transition: transform 0.5s ease, opacity 1s ease;
`;

const VideoText = styled(Text)`
  color: ${CELL_COLOR};
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TextComponent = styled.p`
  color: ${CELL_COLOR};
  font-size: 13px;
  white-space: nowrap;
  text-align: left;
  width: 100%;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Display names only (Aaron, 5 Nov 2026). The stored values stay preview / master / CAD /
// distribution, so no file record changes. Editable in Settings later.
const dropdownItems: DropdownItemsType[] = [
  { label: 'Preview', value: 'preview' },
  { label: 'Master', value: 'master' },
  { label: 'Approval', value: 'CAD' },
  { label: 'Distribution', value: 'distribution' },
];

type Props = {
  video: IVideo;
  recordID: string;
  isMastersOpen(state: boolean): void;
};

export const VideoComponent: React.FC<Props> = ({ video, recordID, isMastersOpen }) => {
  const [videoPlaying, setVideoPlaying] = useState<IVideo | undefined>(undefined);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState(video.notes ? video.notes : '');
  const { deleteVideo, updateVideo, uploadVideo } = useVideo();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const { addRecord } = useRecords();
  const { records } = useSelector((state: AppState) => state.data);
  const [isMoving, setIsMoving] = useState(false);
  const names = useLabelNames(); // the customer's storage-label names (Admin › Settings)

  const currentRecord = records.find((item) => item.id === recordID);

  useEffect(() => {
    const getUrl = async () => {
      const newURL = await firebase.storage().refFromURL(video.objectRef).getDownloadURL();
      setUrl(newURL);
    };
    getUrl();
  }, []);

  const downloadVideo = async () => {
    if (video.category !== 'preview') {
      const confirm = await deleteModal(
        'This is an archived file, downloading will incur significant, monetary cost. Continue?',
      );
      if (!confirm) {
        return;
      }
    }

    const response = await axios.get(url, {
      responseType: 'blob',
      onDownloadProgress: (progress) => {
        const percent = (progress.loaded / progress.total) * 100;
        setDownloadProgress(percent);
      },
    });
    const downloadURL = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = downloadURL;
    link.setAttribute('download', video.id);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const videoPlay = (video: IVideo) => {
    setVideoPlaying(video);
  };

  const updateSelected = (item: DropdownItemsType) => {
    updateVideo(video.id, { ...video, category: item.value as IVideo['category'] });
  };

  const handleSelectProject = (project: string) => {
    setSelectedProject(project);
    setIsModalOpen(false);
  };

  const moveAssetToProject = async (video: IVideo, projectTitle: string) => {
    try {
      setIsMoving(true); // Start showing the "Please wait" message
      const projectsRef = firebase.firestore().collection('projects');
      const querySnapshot = await projectsRef.where('title', '==', projectTitle).get();

      if (querySnapshot.empty) {
        console.error(`Project with title ${projectTitle} not found.`);
        setIsMoving(false); // Stop showing the "Please wait" message if there's an error
        return;
      }

      const projectDoc = querySnapshot.docs[0];
      const projectId = projectDoc.id;

      // Check if a record with the current record's title already exists in the selected project
      const recordsRef = firebase.firestore().collection('records');
      const recordSnapshot = await recordsRef
        .where('projectID', '==', projectId)
        .where('title', '==', currentRecord?.title)
        .get();

      let recordId;
      if (!recordSnapshot.empty) {
        const existingRecord = recordSnapshot.docs[0];
        recordId = existingRecord.id;
      } else {
        recordId = addRecord(projectId, currentRecord?.title);
      }

      // Get current asset document
      const videoRef = firebase.firestore().collection('videos').doc(video.id);

      // Upload the asset to the new project
      const url = await firebase.storage().refFromURL(video.objectRef).getDownloadURL();
      const response = await fetch(url);
      const blob = await response.blob();
      const fileName = path.basename(video.objectRef);
      const file = new File([blob], fileName);

      uploadVideo(file, recordId, (upload: firebase.storage.UploadTask) => {
        upload.on(
          'state_changed',
          (snapshot) => {
            // Handle upload progress if needed
          },
          (error) => {
            console.error('Error uploading asset:', error);
            setIsMoving(false); // Stop showing the "Please wait" message if there's an error
          },
          async () => {
            const downloadURL = await upload.snapshot.ref.getDownloadURL();
            await videoRef.update({
              project: firebase.firestore().doc(`projects/${projectId}`),
              url: downloadURL,
            });
            alert(`Video with ID ${video.id} moved to project with ID ${projectId}.`);
            setIsMoving(false); // Stop showing the "Please wait" message after the upload is complete
          },
        );
      });
    } catch (error) {
      console.error('Error moving video to project:', error);
      setIsMoving(false); // Stop showing the "Please wait" message if there's an error
    }
  };

  useEffect(() => {
    if (selectedProject) {
      moveAssetToProject(video, selectedProject); // Move asset when project selected
    }
  }, [selectedProject]);

  return (
    <>
      {videoPlaying && (
        <Modal>
          <VideoPlayer
            video={videoPlaying}
            exit={() => setVideoPlaying(undefined)}
            downloadVideo={downloadVideo}
            url={url}
            projectID={currentRecord?.projectID}
          />
        </Modal>
      )}

      <Container isPreview={video.category === 'preview'}>
        <LoadingBar percent={downloadProgress} />
        {/* archy-vrow: on a phone the row becomes a small card (index.css) */}
        <VideoBar className="archy-vrow" style={videoGrid}>
          {/* UI refresh: play for previews; masters show "on ice"; other labels a file mark */}
          {video.category === 'preview' ? (
            <RowIconButton label="Play" onClick={() => videoPlay(video)}>
              <PlayIcon />
            </RowIconButton>
          ) : (
            <span
              title="Cold storage: downloads as fast as usual, with a small retrieval fee per download"
              style={{
                width: 32,
                height: 32,
                flex: 'none',
                borderRadius: 8,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#DCEBFA',
              }}
            >
              {/* every non-preview label is cold storage */}
              <IceIcon />
            </span>
          )}
          {/* filename, with a small reminder on previews only */}
          <div className="archy-v-name" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <VideoText title={video.id}>{path.basename(video.id, path.extname(video.id))}</VideoText>
            {video.category === 'preview' && (
              <WarningDot
                role="img"
                aria-label={`Reminder: if this is actually a master, change its label to ${names.master}`}
                title={`Reminder: if this is actually a master, change its label to ${names.master} so it moves to cold storage.`}
              >
                ?
              </WarningDot>
            )}
          </div>
          <NotesInput
            className="archy-v-notes"
            value={notes}
            placeholder={'Notes / details'}
            type="text"
            aria-label="Notes"
            onChange={(input: any) => setNotes(input.target.value)}
            onBlur={(input: any) => updateVideo(video.id, { notes: input.target.value })}
          />
          {/* file type: plain text like the other columns */}
          <TextComponent className="archy-v-meta">{path.extname(video.id).replace('.', '').toUpperCase() || '—'}</TextComponent>
          <TextComponent className="archy-v-meta">{prettyBytes(parseInt(video.size as any) || 0)}</TextComponent>
          <div className="archy-v-label" style={{ minWidth: 0 }}>
            <CategorySelect value={video.category} onChange={(value) => updateSelected({ label: value, value })} />
          </div>
          <TextComponent className="archy-v-meta archy-v-date">{moment.unix(video.date).format('DD-MM-YY')}</TextComponent>
          <div className="archy-v-actions" style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <RowIconButton label="Download" onClick={downloadVideo}>
              <DownloadIcon />
            </RowIconButton>
            <RowIconButton label="Move to another project" onClick={() => setIsModalOpen(true)}>
              <MoveIcon />
            </RowIconButton>
            <RowIconButton label="Delete" danger onClick={() => deleteVideo(video.id, video.category === 'preview')}>
              <DeleteIcon color="#B3261E" size={16} />
            </RowIconButton>
          </div>
        </VideoBar>
      </Container>
      {isModalOpen && <ProjectModal onClose={() => setIsModalOpen(false)} onSelect={handleSelectProject} />}
      {isMoving && <MovingModal />}
    </>
  );
};
